import assertNever from "assert-never";
import { LocalDate } from "@js-joda/core";
import * as React from "react";
import * as Ons from "react-onsenui";
import { API } from "../API";
import { isLoading, Loading, loading } from "../Loading";
import { OrientationLanguages, TranslationMapping } from "../messages";
import {
  AgencyId,
  CaregiverDocumentTypeId,
  CaregiverDocumentUploadId,
} from "../messages/ObjectIds";
import {
  AgencyDocument,
  AgencyDocumentCreate,
  AgencyDocumentType,
  PassportDocument,
  PassportDocumentCreate,
  PassportDocumentCreateResponse,
  PassportDocumentType,
} from "../messages/UploadedDocuments";
import { STRINGS } from "../strings";
import { globalize, translate } from "../translate";
import AppLoadingPlaceholder from "./Generic/AppLoadingPlaceholder";
import { openDatepicker } from "./MedflytDatepicker";
import PassportUploadActions from "./MedflytPassportDocuments/components/PassportUploadActions/PassportUploadActions";
import { openScanner } from "./MedflytScanner";
import { ScreenSkeleton } from "./ScreenSkeleton";
import { simpleAlert } from "./SimpleAlert";

export interface MedflytPassportDocumentsProps {
  api: API;
  onBackClick: () => void;
  onDocumentViewClick: (url: string | null) => void;
  agencyId: AgencyId | null;
  documentType: PassportDocumentType | AgencyDocumentType;
  language: OrientationLanguages;
}

export interface MedflytPassportDocumentsState {
  documents: (PassportDocument | AgencyDocument)[] | Loading;
  popoverOpen: CaregiverDocumentUploadId | null;
  isLoading: boolean;
  isUploadingDocument: boolean;
  isUploadOptionsOpen: boolean;
}

export class MedflytPassportDocumentsScreen extends React.Component<
  MedflytPassportDocumentsProps,
  MedflytPassportDocumentsState
> {
  constructor(props: MedflytPassportDocumentsProps) {
    super(props);

    this.state = {
      documents: loading(),
      popoverOpen: null,
      isLoading: false,
      isUploadingDocument: false,
      isUploadOptionsOpen: false,
    };

    this.loadDocuments();
  }

  loadDocuments() {
    switch (this.props.documentType.passportCategoryType) {
      case "PASSPORT_CATEGORY": {
        this.props.api.getMedflytPassportDocuments(this.props.documentType.id).then((documents) => {
          this.setState({
            documents: documents.documents,
            isLoading: false,
            isUploadingDocument: false,
          });
        });
        break;
      }
      case "AGENCY_CATEGORY": {
        if (this.props.agencyId === null) {
          throw new Error("Can't get agency passport documents without agency id");
        }
        this.props.api
          .getMedflytAgencyDocuments(this.props.agencyId, this.props.documentType.id)
          .then((documents) => {
            this.setState({
              documents: documents.documents,
              isLoading: false,
              isUploadingDocument: false,
            });
          });
        break;
      }
      default: {
        return;
      }
    }
  }

  removeDocument(docId: CaregiverDocumentUploadId) {
    this.setState({ isLoading: true });

    this.props.api
      .removeMedflytPassportDocument(docId)
      .then(() => {
        if (!isLoading(this.state.documents)) {
          this.setState({
            documents: this.state.documents.filter((currDoc) => currDoc.id !== docId),
            popoverOpen: null,
            isLoading: false,
          });
        }
      })
      .catch(() => {
        this.setState({ isLoading: false });
        simpleAlert({ icon: "error", message: translate(STRINGS.ERROR_REMOVING_DOCUMENT) });
      });
  }

  setDocumentExpiry(docId: CaregiverDocumentUploadId, expiry: LocalDate | undefined) {
    if (expiry !== undefined) {
      this.setState({ isUploadingDocument: true });

      this.props.api
        .setMedflytPassportDocumentExpiryDate(docId, expiry)
        .then(() => {
          if (!isLoading(this.state.documents)) {
            this.setState({ documents: loading(), popoverOpen: null, isUploadingDocument: false });
          }
          this.loadDocuments();
          this.props.api.categoriesUpdated.broadcast();
        })
        .catch(() => {
          this.setState({ isUploadingDocument: false });
          simpleAlert({ icon: "error", message: translate(STRINGS.ERROR_REMOVING_DOCUMENT) });
        });
    }
  }

  datepickerCallbackAfterUpload = (
    datePickerData: LocalDate | undefined,
    PassportDocumentId: CaregiverDocumentUploadId
  ) => {
    if (datePickerData === undefined) {
      this.setState({ documents: loading(), popoverOpen: null, isUploadingDocument: false });
      this.loadDocuments();
      this.props.api.categoriesUpdated.broadcast();
    } else {
      this.setDocumentExpiry(PassportDocumentId, datePickerData);
    }
  };

  getTranslatedStrings = (
    translationMap: TranslationMapping<string>,
    language: OrientationLanguages
  ): string => {
    const defaultLang: OrientationLanguages = "English";

    let labelTranslation = translationMap[language];

    if (labelTranslation === undefined) {
      labelTranslation = translationMap[defaultLang];
    }

    return labelTranslation as string;
  };

  scanSuccessCallback = (fileData: string) => {
    this.setState({ isUploadingDocument: true });

    let uploadPassportRequest: Promise<PassportDocumentCreateResponse>;
    switch (this.props.documentType.passportCategoryType) {
      case "PASSPORT_CATEGORY": {
        uploadPassportRequest = this.uploadPassportCategoryDocument(
          this.getTranslatedStrings(this.props.documentType.categoryName, this.props.language),
          fileData
        );
        break;
      }
      case "AGENCY_CATEGORY": {
        if (this.props.agencyId === null) {
          throw new Error("Can't upload agency passport document without agency id");
        }
        uploadPassportRequest = this.uploadAgencyPassportCategoryDocument(
          this.props.agencyId,
          fileData
        );
        break;
      }
      default: {
        return;
      }
    }

    uploadPassportRequest
      .then((response: PassportDocumentCreateResponse) => {
        this.setState({ isUploadingDocument: false });
        if (this.props.documentType.isExpires) {
          openDatepicker(
            {
              type: "date",
              callbackObj: {
                cancelable: true,
                callback: (datepickerData) =>
                  this.datepickerCallbackAfterUpload(datepickerData, response.id),
              },
            },
            translate(STRINGS.SET_DOCUMENT_EXPIRY_DATE)
          );
        } else {
          this.setState({ documents: loading(), popoverOpen: null, isUploadingDocument: false });
          this.loadDocuments();
          this.props.api.categoriesUpdated.broadcast();
        }
      })
      .catch((err: any) => {
        this.setState({ isUploadingDocument: false });
        console.log(err);
      });
  };

  uploadPassportCategoryDocument = (
    categoryName: string,
    fileData: string
  ): Promise<PassportDocumentCreateResponse> => {
    const data: PassportDocumentCreate = {
      base64: fileData,
      documentName: this.getTranslatedStrings(
        this.props.documentType.documentName,
        this.props.language
      ),
      categoryId: CaregiverDocumentTypeId.unwrap(this.props.documentType.id),
      categoryName: categoryName,
      documentTypeId: this.props.documentType.id,
      expiryDate: null,
    };

    return this.props.api.uploadMedflytPassportDocument(data);
  };

  uploadAgencyPassportCategoryDocument = (agencyId: AgencyId, fileData: string) => {
    const data: AgencyDocumentCreate = {
      base64: fileData,
      expiryDate: null,
    };

    return this.props.api.uploadMedflytPassportAgencyDocument(
      agencyId,
      this.props.documentType.id,
      data
    );
  };

  scanErrorCallback = (message: string) => {
    switch (message) {
      case "Scan was cancelled": {
        return;
      }
      default: {
        simpleAlert({
          icon: "error",
          message: translate(STRINGS.UPLOAD_ERROR),
          title: translate(STRINGS.ERROR),
          cancelable: true,
        });
      }
    }
  };

  handleUploadClick = () => {
    openScanner(this.scanSuccessCallback, this.scanErrorCallback, {});
  };

  handleUploadFromDevice = (dataUri: string) => {
    return this.scanSuccessCallback(dataUri);
  };

  handleUploadOptions = (isOpen: boolean) => {
    this.setState({ isUploadOptionsOpen: isOpen });
  };

  renderDocumentStatus(doc: PassportDocument) {
    let color: string = "green";
    switch (doc.status) {
      case "APPROVED":
        color = "green";
        break;
      case "DECLINED":
        color = "red";
        break;
      case "PENDING":
        color = "#ff7d00";
        break;
      default:
        assertNever(doc.status);
    }
    return (
      <span style={{ color: color, textTransform: "capitalize" }}>{doc.status.toLowerCase()}</span>
    );
  }

  renderSingleDocument(doc: PassportDocument | AgencyDocument) {
    return (
      <div
        className="MedflytPassportDocumentsScreen_document"
        key={CaregiverDocumentUploadId.unwrap(doc.id)}
      >
        <span className="MedflytPassportDocumentsScreen_documentCreatedAt">
          {globalize().formatInstant(doc.createdAt, { skeleton: "yMMMd" })}
        </span>
        <span style={{ width: 15 }} />
        {doc.expiryDate !== null ? (
          <span className="MedflytPassportDocumentsScreen_documentExpiryDate">
            {translate(STRINGS.EXPIRES_AT) +
              " " +
              globalize().formatLocalDate(doc.expiryDate, { skeleton: "yMMMd" })}
          </span>
        ) : (
          ""
        )}
        <span style={{ width: 15, flexGrow: 1 }} />
        {this.props.documentType.passportCategoryType === "PASSPORT_CATEGORY"
          ? this.renderDocumentStatus(doc as PassportDocument)
          : null}
        <span style={{ width: 5 }} />
        <div className="MedflytPassportDocumentsScreen_documentPopoverContainer">
          <img
            src="assets/passport-popover-dots.svg"
            width="20"
            height="20"
            onClick={() => this.setState({ popoverOpen: doc.id })}
          />
          {this.state.popoverOpen === doc.id ? (
            <div style={{ position: "relative" }}>
              <>
                <div
                  className="MedflytPassportDocumentsScreen_documentPopoverOverlay"
                  onClick={() => this.setState({ popoverOpen: null })}
                />
                <div className="MedflytPassportDocumentsScreen_documentPopover">
                  <div onClick={() => this.props.onDocumentViewClick(doc.fileUrl)}>
                    {translate(STRINGS.VIEW)}
                  </div>
                  {this.props.documentType.isExpires ? (
                    <div
                      onClick={() =>
                        openDatepicker(
                          {
                            type: "date",
                            callbackObj: {
                              cancelable: true,
                              callback: (data) => this.setDocumentExpiry(doc.id, data),
                            },
                          },
                          translate(STRINGS.SET_DOCUMENT_EXPIRY_DATE)
                        )
                      }
                    >
                      {translate(STRINGS.SET_EXPIRATION_DATE)}
                    </div>
                  ) : null}
                  <div onClick={() => this.removeDocument(doc.id)}>{translate(STRINGS.REMOVE)}</div>
                </div>
              </>
            </div>
          ) : (
            ""
          )}
        </div>
        <span />
      </div>
    );
  }

  render() {
    return (
      <ScreenSkeleton
        className="MedflytPassportDocumentsScreen"
        rootClassName="UploadDocFilesScreen_background"
        title={this.getTranslatedStrings(this.props.documentType.documentName, this.props.language)}
        titleBarStyle={ScreenSkeleton.TitleBarStyle.PRIMARY}
        onBackClick={this.props.onBackClick}
      >
        {isLoading(this.state.documents) || this.state.isLoading ? (
          <div className="VisitsScreen__loadingCont">
            <Ons.ProgressCircular indeterminate={true} />
            <div>{translate(STRINGS.LOADING)}...</div>
          </div>
        ) : (
          <>
            {this.state.documents.length > 0 ? (
              <div
                className="MedflytPassportDocumentsScreen_documentContainer"
                style={{ marginTop: 10 }}
              >
                {this.state.documents.map((curr) => {
                  return this.renderSingleDocument(curr);
                })}
              </div>
            ) : (
              <div className="MedflytPassportDocumentsScreen_placeholder">
                <img width="120" src="assets/medflyt-passport-placeholder.svg" />
                <span className="MedflytPassportDocumentsScreen_placeholderTitle">
                  {this.getTranslatedStrings(
                    this.props.documentType.documentName,
                    this.props.language
                  ) +
                    " " +
                    translate(STRINGS.SHOULD_BE_HERE)}
                </span>
                <span className="MedflytPassportDocumentsScreen_placeholderText">
                  {translate(STRINGS.YOU_HAVENT_UPLOADED_ANYTHING_YET)}
                </span>
              </div>
            )}
          </>
        )}
        <PassportUploadActions
          documentName={
            translate(STRINGS.UPLOAD) +
            " " +
            this.getTranslatedStrings(this.props.documentType.documentName, this.props.language)
          }
          isUploadOptionsOpen={this.state.isUploadOptionsOpen}
          onUploadOptionsOpen={(isOpen: boolean) => this.handleUploadOptions(isOpen)}
          onOpenPassportScanner={this.handleUploadClick}
          onUploadFromDevice={(dataUri) => this.handleUploadFromDevice(dataUri)}
        />
        <AppLoadingPlaceholder
          primaryHeader={translate(STRINGS.UPLOADING)}
          secondaryHeader={translate(STRINGS.PLEASE_WAIT) + "..."}
          isOpen={this.state.isUploadingDocument}
        />
      </ScreenSkeleton>
    );
  }
}
